import { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageHero from "../components/PageHero";
import { Mail, Phone, MapPin, Send, Clock, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { SITE } from "../data/mock";

const LOCATIONS = [
  { name: "Orange County Center", area: "Orlando", note: "Programs Director: Victoria H" },
  { name: "Seminole County Center", area: "Sanford", note: "Programs Manager: Jill A" },
  { name: "Brevard County Center", area: "Titusville / Melbourne", note: "Programs Manager: Daniel C" },
  { name: "Osceola County Center", area: "Kissimmee", note: "Lead Peer: Crystal H" },
];

const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", topic: "general", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      toast.success("Thank you. A peer specialist will reach out shortly.");
      setForm({ name: "", email: "", phone: "", topic: "general", message: "" });
    }, 700);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main>
        <PageHero
          eyebrow="Contact"
          title="Let’s talk."
          description="Whether you’re seeking support, exploring a partnership, or just have a question — we’re here to listen. All inquiries are confidential."
          breadcrumbs={[{ label: "Contact" }]}
        />

        <section className="py-20 md:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            {/* Info */}
            <div className="lg:col-span-5">
              <div className="space-y-5">
                <a href={`tel:${SITE.helplineTel}`} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center shrink-0 group-hover:bg-[hsl(var(--rc-deep))] group-hover:text-white text-[hsl(var(--rc-purple))] transition-colors">
                    <Phone className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <div className="text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))]">Helpline</div>
                    <div className="font-serif-display text-[20px] font-semibold text-[hsl(var(--rc-deep))] group-hover:text-[hsl(var(--rc-purple))] transition-colors">
                      {SITE.helpline} ({SITE.helplineDigits})
                    </div>
                  </div>
                </a>

                <a href={`mailto:${SITE.email}`} className="flex items-start gap-4 group">
                  <div className="w-11 h-11 rounded-xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center shrink-0 group-hover:bg-[hsl(var(--rc-deep))] group-hover:text-white text-[hsl(var(--rc-purple))] transition-colors">
                    <Mail className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <div className="text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))]">Email</div>
                    <div className="font-serif-display text-[20px] font-semibold text-[hsl(var(--rc-deep))] group-hover:text-[hsl(var(--rc-purple))] transition-colors">
                      {SITE.email}
                    </div>
                  </div>
                </a>

                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 rounded-xl bg-[hsl(var(--rc-purple-soft))] flex items-center justify-center shrink-0 text-[hsl(var(--rc-purple))]">
                    <Clock className="w-4.5 h-4.5" />
                  </div>
                  <div>
                    <div className="text-[12px] tracking-[0.18em] uppercase text-[hsl(var(--muted-foreground))]">Helpline Hours</div>
                    <div className="font-serif-display text-[20px] font-semibold text-[hsl(var(--rc-deep))]">24/7</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 p-6 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--rc-paper))]">
                <div className="flex items-center gap-3 mb-4">
                  <Building2 className="w-5 h-5 text-[hsl(var(--rc-purple))]" />
                  <h3 className="font-serif-display text-[18px] font-semibold text-[hsl(var(--rc-deep))]">
                    Community Centers
                  </h3>
                </div>
                <ul className="divide-y divide-[hsl(var(--border))]">
                  {LOCATIONS.map((l) => (
                    <li key={l.name} className="py-3 first:pt-0 last:pb-0 flex items-start gap-3">
                      <MapPin className="w-4 h-4 mt-0.5 text-[hsl(var(--rc-purple))] shrink-0" />
                      <div>
                        <div className="text-[14px] font-semibold text-[hsl(var(--rc-charcoal))]">{l.name}</div>
                        <div className="text-[12.5px] text-[hsl(var(--muted-foreground))]">{l.area} · {l.note}</div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Form */}
            <div className="lg:col-span-7">
              <form onSubmit={onSubmit} className="bg-white border border-[hsl(var(--border))] rounded-2xl p-7 md:p-10 shadow-[0_20px_50px_-30px_rgba(60,40,90,0.2)]">
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">Name</label>
                    <Input value={form.name} onChange={handle("name")} placeholder="Your full name" className="h-12 bg-white border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--rc-purple))] focus-visible:border-[hsl(var(--rc-purple))]" />
                  </div>
                  <div>
                    <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">Email</label>
                    <Input type="email" value={form.email} onChange={handle("email")} placeholder="you@example.com" className="h-12 bg-white border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--rc-purple))] focus-visible:border-[hsl(var(--rc-purple))]" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-5 mt-5">
                  <div>
                    <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">Phone (optional)</label>
                    <Input value={form.phone} onChange={handle("phone")} placeholder="(407) 000-0000" className="h-12 bg-white border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--rc-purple))] focus-visible:border-[hsl(var(--rc-purple))]" />
                  </div>
                  <div>
                    <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">Topic</label>
                    <select
                      value={form.topic}
                      onChange={handle("topic")}
                      className="h-12 w-full rounded-md border border-[hsl(var(--border))] bg-white px-3 text-[14px] text-[hsl(var(--rc-charcoal))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--rc-purple))]"
                    >
                      <option value="general">General inquiry</option>
                      <option value="support">Recovery support</option>
                      <option value="family">Family support</option>
                      <option value="partner">Partnership</option>
                      <option value="volunteer">Volunteer</option>
                      <option value="media">Media / press</option>
                    </select>
                  </div>
                </div>
                <div className="mt-5">
                  <label className="block text-[12px] tracking-[0.16em] uppercase text-[hsl(var(--muted-foreground))] mb-2">How can we help?</label>
                  <Textarea value={form.message} onChange={handle("message")} placeholder="Share a little about what you’re looking for. All inquiries are confidential." rows={6} className="bg-white border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--rc-purple))] focus-visible:border-[hsl(var(--rc-purple))]" />
                </div>
                <div className="mt-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-[12px] text-[hsl(var(--muted-foreground))] max-w-md">
                    By submitting, you agree to be contacted by a member of our team. We respect your privacy.
                  </p>
                  <Button type="submit" disabled={submitting} className="bg-[hsl(var(--rc-deep))] hover:bg-[hsl(var(--rc-purple))] text-white rounded-full h-12 px-7">
                    {submitting ? "Sending…" : (
                      <span className="flex items-center">Send message <Send className="w-4 h-4 ml-2" /></span>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
